/**
 * MenuItem.jsx — 사이드바 개별 메뉴 링크 컴포넌트
 *
 * 평소: react-router Link 로 라우트 이동
 * editMode: button + 체크박스 — 클릭 시 라우트 이동 차단, toggle(path) 호출 (2026-04-27 신규)
 *
 * editMode 는 SidebarEditContext 에서 자동 주입 — props drilling 불필요.
 * Provider 미설정 시 editMode=false 기본값 → 평소 동작 (회귀 zero).
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useSidebarEdit } from './SidebarEditContext';
import { rememberLastScreen } from '../../../utils/lastScreen';

// PC 즐겨찾기 별 (2026-10-22) — 줄에 마우스를 올리면 오른쪽 끝에 보인다(사용자 선택). 즐겨찾기 칸의 줄은 ★(누르면 빠짐).
//   5칸이 찼으면 흐리게 + 안내 글(title). 링크 안에 단추를 넣을 수 없어(a 안의 button) 옆에 겹쳐 놓는다.
function FavoriteStar({ fav, to, label, favEntry }) {
  const starred = favEntry ? true : fav.isStarred(to);
  const blocked = !starred && !fav.canAdd;
  const title = starred ? '즐겨찾기에서 빼기'
    : (blocked ? `즐겨찾기는 ${fav.max}개까지예요 — 하나 빼고 넣어 주세요` : '즐겨찾기에 넣기');
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={starred}
      aria-disabled={blocked || undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (blocked) return;
        if (favEntry) fav.remove(favEntry); else fav.toggle({ to, label });
      }}
      className={cn(
        'absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center',
        'border-none bg-transparent opacity-0 focus-visible:opacity-100 transition-opacity duration-100',
        blocked ? 'cursor-not-allowed group-hover/fav:opacity-40 text-[var(--text-muted)]'
          : 'cursor-pointer group-hover/fav:opacity-100 hover:bg-black/5 dark:hover:bg-white/10',
        !blocked && (starred ? 'text-amber-500 dark:text-amber-400'
          : 'text-[var(--text-muted)] hover:text-amber-500 dark:hover:text-amber-400'),
      )}
    >
      <Star size={13} strokeWidth={2.2} fill={starred ? 'currentColor' : 'none'} />
    </button>
  );
}

/**
 * @param {string}   to       — 이동 경로 (Link) / 식별자 (editMode)
 * @param {string}   label    — 표시 레이블
 * @param {function} Icon     — Lucide 아이콘 컴포넌트
 * @param {boolean}  isActive — 현재 경로 일치 시 강조 (editMode 무관)
 * @param {function} onClick  — 평소 클릭 핸들러 (모바일 메뉴 닫기 등)
 * @param {node}     badges   — (선택) 이름 오른쪽 표시. 통합 메뉴의 회사(P·D·L) — 없으면 종전과 똑같이 그린다 (2026-10-01)
 * @param {boolean}  editable — false 면 바로가기 편집에서 고를 수 없다(흐리게). 기본 true.
 *                              (2026-10-24 통합 메뉴도 상단 바가 찾게 되어(resolveShortcut) 지금 false 를 넘기는 곳은 없다)
 * @param {boolean}  favoritable — false 면 PC 즐겨찾기 별을 달지 않는다(홈·상황판 — 이미 맨 위 고정). 기본 true.
 * @param {object}   favEntry — 즐겨찾기 칸의 줄이면 그 칸(별 = 빼기).
 */
export default function MenuItem({
  to, label, Icon: IconComp, isActive, onClick, badges = null, editable = true, favoritable = true, favEntry = null,
}) {
  const { editMode, selections, toggle, fav } = useSidebarEdit();
  const isSelected = editMode && selections.has(to);
  // 마지막 작업 화면(2026-10-26) — 왼쪽 메뉴에 보이는 줄이 지금 화면이면 그 주소를 기억(이 기기·이 계정)
  useEffect(() => { if (isActive) rememberLastScreen(to); }, [isActive, to]);

  const baseClass = cn(
    'relative flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-xs font-semibold',
    'transition-colors duration-100 no-underline',
    // 2026-08-26 흰 사이드바: 어두운 전용색 → 테마 변수/다크 변형 (다크 모드 기존 톤 유지)
    editMode
      ? (isSelected ? 'bg-accent-500/20 text-accent-600 dark:text-accent-200' : 'text-[var(--text-sub)] hover:bg-black/5 dark:hover:bg-white/5')
      : (isActive
        ? 'bg-accent-500/10 text-accent-600 dark:text-accent-300'
        : 'text-[var(--text-sub)] hover:bg-black/5 dark:hover:bg-white/5 hover:text-[var(--text)]'),
  );

  const trailing = badges ? <span className="ml-auto flex-shrink-0 flex items-center gap-0.5">{badges}</span> : null;

  // 편집 모드 — 바로가기에 넣을 수 없는 메뉴는 흐리게만
  if (editMode && !editable) {
    return (
      <div className={cn(baseClass, 'opacity-40 cursor-not-allowed')} aria-disabled="true">
        <IconComp size={14} className="flex-shrink-0" />
        <span className="truncate">{label}</span>
        {trailing}
      </div>
    );
  }

  // 편집 모드 — Link 대신 button, toggle 만 호출
  if (editMode) {
    return (
      <button type="button" onClick={() => toggle(to)} className={cn(baseClass, 'w-full text-left border-none cursor-pointer bg-transparent')}>
        <span className={cn(
          'inline-flex items-center justify-center w-3.5 h-3.5 rounded border flex-shrink-0',
          isSelected ? 'bg-accent-500 border-accent-500' : 'border-slate-500',
        )}>
          {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
        </span>
        <IconComp size={14} className="flex-shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    );
  }

  // 평소 모드 — Link. PC 즐겨찾기가 켜져 있으면 마우스를 올렸을 때 별 자리만큼 글자를 비킨다.
  const starOn = Boolean(fav?.enabled) && favoritable;
  const link = (
    <Link to={to} onClick={onClick} className={cn(baseClass, starOn && 'group-hover/fav:pr-8 group-focus-within/fav:pr-8')}>
      <span
        className={cn(
          'absolute left-0 top-[18%] bottom-[18%] w-[3px] rounded-full bg-accent-400',
          'transition-opacity duration-100',
          isActive ? 'opacity-100' : 'opacity-0',
        )}
      />
      <IconComp size={14} className="flex-shrink-0" />
      <span className="truncate">{label}</span>
      {trailing}
    </Link>
  );
  if (!starOn) return link;
  return (
    <div className="group/fav relative">
      {link}
      <FavoriteStar fav={fav} to={to} label={label} favEntry={favEntry} />
    </div>
  );
}
